import { Card } from "@/components/layout";
import {
  MetricKpiTile,
  PanelError,
  PanelLoading,
  SectionHeading,
  panelErrorTitle,
} from "@/components/dashboard";
import { DonutChart, HorizontalBarList, type DonutTone } from "@/components/charts";
import { useGa4Overview, useGa4Country, useGa4Device, useGa4Realtime, type Ga4Device } from "@/hooks/useGa4";
import type { TimeRange } from "@/lib/time-range";

const DEVICE_TONES: DonutTone[] = ["sky", "emerald", "amber", "slate", "red"];

function pct(v: number): string {
  return `${(v * 100).toFixed(1)}%`;
}
function duration(sec: number): string {
  const m = Math.floor(sec / 60);
  const s = Math.round(sec % 60);
  return m > 0 ? `${m}m ${s}s` : `${s}s`;
}

interface Props {
  range: TimeRange;
  device?: Ga4Device | null;
}

export function Ga4AudienceSection({ range, device: segment = null }: Props) {
  // Headline KPIs reflect the selected segment; the breakdowns stay full so the
  // device/country mix is always visible (and pickable).
  const overview = useGa4Overview(range, { device: segment });
  const country = useGa4Country(range);
  const device = useGa4Device(range);
  const realtime = useGa4Realtime();

  const o = overview.data;
  const ovErr = overview.error ? panelErrorTitle(overview.error) : undefined;

  return (
    <section className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <SectionHeading
          title="GA4 Audience"
          source="ga4"
          description="Everyone who visited signatureaviation.com — across every page and traffic source. These are the headline numbers you'd see in Google Analytics itself."
        />
        {realtime.data && (
          <span
            className="inline-flex items-center gap-1.5 rounded-full bg-emerald-500/10 px-2.5 py-1 text-xs font-medium text-emerald-300 ring-1 ring-emerald-500/30"
            title="People active on the site in the last 30 minutes, refreshed every 30s (GA4 Realtime API)."
          >
            <span className="relative flex h-2 w-2">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" />
              <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-400" />
            </span>
            {realtime.data.activeUsers.toLocaleString("en-US")} active now
          </span>
        )}
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <MetricKpiTile label="Total visitors" value={o ? o.totalUsers.toLocaleString("en-US") : "—"}
          delta={o ? `${o.activeUsers.toLocaleString("en-US")} active in range` : undefined} tone="sky"
          hint="Every distinct person who visited in this period (GA4 'total users'). 'Active' = those who actually engaged, not just loaded a page."
          isLoading={overview.isLoading} errorTitle={ovErr} />
        <MetricKpiTile label="New vs returning" value={o ? o.newUsers.toLocaleString("en-US") : "—"}
          delta={o ? `new · ${o.returningUsers.toLocaleString("en-US")} returning` : undefined} tone="emerald"
          hint="First-time visitors vs people who'd been before. Most aviation traffic is new — a returning user is a strong loyalty signal."
          isLoading={overview.isLoading} errorTitle={ovErr} />
        <MetricKpiTile label="Sessions" value={o ? o.sessions.toLocaleString("en-US") : "—"}
          delta={o ? `${o.screenPageViews.toLocaleString("en-US")} page views` : undefined} tone="neutral"
          hint="A session is one visit (a burst of activity). One visitor can have several sessions. Page views counts every page opened."
          isLoading={overview.isLoading} errorTitle={ovErr} />
        <MetricKpiTile label="Engagement" value={o ? pct(o.engagementRate) : "—"}
          delta={o ? `~${duration(o.avgEngagementTimeSec ?? 0)} per visitor · ${pct(o.bounceRate)} bounce` : undefined} tone="amber"
          hint="Engagement rate = share of visits where someone actually did something (scrolled, clicked, stayed >10s). The time is average active seconds per visitor — this is the '34s' Google Analytics shows, not raw time-on-page."
          isLoading={overview.isLoading} errorTitle={ovErr} />
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <Card title="Top countries by users">
          {country.isLoading ? <PanelLoading />
            : country.error ? <PanelError error={country.error} onRetry={() => country.refetch()} />
            : <HorizontalBarList tone="sky" limit={10}
                entries={(country.data?.slices ?? []).map((s) => ({ label: s.label, value: s.users }))} />}
        </Card>

        <Card title="Users by device">
          {device.isLoading ? <PanelLoading />
            : device.error ? <PanelError error={device.error} onRetry={() => device.refetch()} />
            : <DonutChart centerLabel="Devices"
                slices={(device.data?.slices ?? []).slice(0, 5).map((s, i) => ({
                  label: s.label, value: s.users, tone: DEVICE_TONES[i % DEVICE_TONES.length]!,
                }))} />}
        </Card>
      </div>
    </section>
  );
}
